import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type StaticDecode, Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import { UsageUser } from 'components/verticals/reference/shared/schema';
import { type LogEntry, useLogUpdatedCVs } from 'stores/ActivityLogs';
import { http, httpChecked, queryClient } from 'util/fetch';
import { invalidateCustomer } from './Customers';

const CustomerDescription = Type.Object({
  customer_description: Type.Readonly(Type.String()),
  count: Type.Readonly(Type.Integer()),
  users: Type.Readonly(Type.Array(UsageUser)),
});

const checker = TypeCompiler.Compile(Type.Array(CustomerDescription));

type CustomerDescription = StaticDecode<typeof CustomerDescription>;
type CustomerDescriptions = readonly CustomerDescription[];

const queries = {
  base: ['customer-descriptions'] as const,
  fetch: (customer_id: string, language_code: string) =>
    queryOptions({
      queryKey: [...queries.base, { customer_id }, { language_code }] as const,
      queryFn: ({ signal }) =>
        httpChecked.get(
          `/api/v2/company/cv/customers/unapproved/project_experiences/customer_description?country_code=${language_code}&customer_id=${customer_id}`,
          checker,
          { signal },
        ),
    }),
};

export function useCustomerDescriptions(
  customer_id: string,
  language_code: string,
): CustomerDescriptions | undefined {
  const { data } = useQuery({
    ...queries.fetch(customer_id, language_code),
    staleTime: 60_000,
  });

  return data;
}

export function usePollingCustomerDescriptions(
  customer_id: string,
  language_code: string,
  enabled: boolean,
) {
  useQuery({
    ...queries.fetch(customer_id, language_code),
    refetchInterval: 1_000,
    enabled,
  });
}

interface ChangeCustomerDescription {
  original_value: string;
  new_value: string;
  customer_id: string;
  language_code: string;
  limit_to_user_ids: readonly string[];
}

export function useChangeCustomerDescription() {
  const queryClient = useQueryClient();
  const logUpdatedCVs = useLogUpdatedCVs();

  return useMutation({
    mutationFn: ({ language_code, ...body }: ChangeCustomerDescription) =>
      http.put<{ updated_cvs: readonly LogEntry[] }>(
        `/api/v2/update-terms/${language_code}/project_experiences/customer_description`,
        { body },
      ),
    onSuccess: ({ updated_cvs }) => logUpdatedCVs(updated_cvs),
    onSettled: (_data, _error, { customer_id, language_code }) =>
      Promise.all([
        queryClient.invalidateQueries(
          queries.fetch(customer_id, language_code),
        ),
        invalidateCustomer(customer_id),
      ]),
  });
}

export async function prefetchCustomerDescriptions(
  customer_id: string,
  language_code: string,
) {
  await queryClient.prefetchQuery({
    ...queries.fetch(customer_id, language_code),
    staleTime: 60_000,
  });
}

export async function invalidateCustomerDescriptions(customer_id: string) {
  // language will be popped off
  const { queryKey } = queries.fetch(customer_id, 'fake');
  const allCustomerKey = queryKey.slice(0, -1);

  await queryClient.invalidateQueries({ queryKey: allCustomerKey });
}

export async function invalidateAllCustomerDescriptions() {
  await queryClient.invalidateQueries({ queryKey: queries.base });
}
